import {
  Col,
  Divider,
  Row,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import Table from 'components/Table';
import PropTypes from 'prop-types';
import React from 'react';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import currencyFormatter from 'utils/toCurrency';

const { Text } = Typography;

const renderImporte = (record) => {
  const value = (record.adeudo * 100) / 100;

  // Caso 1: Importe modificado
  if (record?.importe_original !== record?.importe_de_la_parcialidad) {
    const diff = record.importe_original - record.importe_de_la_parcialidad;
    return (
      <>
        <Tag color="blue" style={{ margin: 0 }}>
          <Tooltip title="Importe original">
            {currencyFormatter(record.importe_original)}
          </Tooltip>
        </Tag>
        {' - '}
        <Tag color="red" style={{ margin: 0 }}>
          <Tooltip title="Importe abonado">
            {currencyFormatter(diff)}
          </Tooltip>
        </Tag>
        {' = '}
        {currencyFormatter(value)}
      </>
    );
  }

  // Caso 2: Parcialidad saldada
  if (record.saldado) {
    return (
      <>
        <Tag color="success">
          Abonado:
          {' '}
          {currencyFormatter(record.total_abonado)}
        </Tag>
        {' '}
        <span>{currencyFormatter(0)}</span>
      </>
    );
  }

  // Caso 3: Parcialidad con sobrante
  if (record?.sobrante) {
    const sobrante = (record.sobrante * 100) / 100;
    const result = (value - sobrante).toFixed(2);

    if (result === '0.00') {
      return currencyFormatter(value);
    }

    return (
      <>
        {currencyFormatter(value)}
        <Tooltip title="Abono a la próxima mensualidad con el sobrante.">
          <span className="danger">
            {' - '}
            {currencyFormatter(record.sobrante)}
          </span>
        </Tooltip>
        <span className="success">
          {' = '}
          {currencyFormatter(result)}
        </span>
      </>
    );
  }

  // Caso 4: Parcialidad normal con abono parcial
  return (
    <>
      {record?.total_abonado > 0 && !record?.saldado && (
        <Tag color="success">
          Abonado:
          {' '}
          {currencyFormatter(record.total_abonado)}
        </Tag>
      )}
      <Tooltip title={`Importe original (${currencyFormatter(record.importe_original)})`}>
        {currencyFormatter(value)}
      </Tooltip>
    </>
  );
};

const getRowClassName = ({ saldado }) => (saldado ? 'disabled' : '');

const ParcialidadesDeConvenios = ({ permission, data }) => {
  const columns = [
    {
      titleText: 'Mensualidad',
      dataIndex: 'mensualidad',
      key: 'mensualidad',
      render: (value) => (value === 0 ? 'Anticipo' : value),
    },
    {
      titleText: 'Fecha de pago',
      dataIndex: 'fecha_de_pago',
      key: 'fecha_de_pago',
      render: (value) => value?.format(DATE_FRONTEND_FORMAT),
    },
    {
      titleText: 'Fecha de vencimiento',
      dataIndex: 'fecha_de_vencimiento',
      key: 'fecha_de_vencimiento',
      render: (value) => value?.format(DATE_FRONTEND_FORMAT),
    },
    {
      titleText: 'Importe',
      dataIndex: 'adeudo',
      key: 'adeudo',
      align: 'right',
      render: (_, record) => renderImporte(record),
    },
    {
      titleText: 'Recibos',
      dataIndex: 'lista_recibos',
      key: 'lista_recibos',
      render: (value = []) => value.map((folio) => (
        <Tag key={folio}>
          {folio}
        </Tag>
      )),
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <br />
      <Divider orientation="left" style={{ marginTop: 30 }}>
        <Text>Parcialidades de Convenio</Text>
      </Divider>
      <Col span={24}>
        <Table
          dataSource={data}
          cols={columns}
          permission={permission}
          allowSort={false}
          allowSearch={false}
          allowColumnSelector={false}
          hasAllViewPermission={permission.hasAllViewPermission}
          rowClassName={getRowClassName}
        />
      </Col>
    </Row>
  );
};

ParcialidadesDeConvenios.propTypes = {
  permission: PropTypes.shape({
    add: PropTypes.bool,
    view: PropTypes.bool,
    delete: PropTypes.bool,
    hasAllViewPermission: PropTypes.bool,
  }).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    adeudo: PropTypes.number,
    importe_original: PropTypes.number,
    importe_de_la_parcialidad: PropTypes.number,
    total_abonado: PropTypes.number,
    sobrante: PropTypes.number,
    saldado: PropTypes.bool,
  })).isRequired,
};

export default ParcialidadesDeConvenios;
